/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { pluginsRotateObservabilityCredential } from "../funcs/pluginsRotateObservabilityCredential.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RotateObservabilityCredentialResult } from "../models/components/rotateobservabilitycredentialresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RotateObservabilityCredentialRequest,
  RotateObservabilityCredentialSecurity,
} from "../models/operations/rotateobservabilitycredential.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RotateObservabilityCredentialMutationVariables = {
  request: RotateObservabilityCredentialRequest;
  security?: RotateObservabilityCredentialSecurity | undefined;
  options?: RequestOptions;
};

export type RotateObservabilityCredentialMutationData =
  RotateObservabilityCredentialResult;

export type RotateObservabilityCredentialMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * rotateObservabilityCredential plugins
 *
 * @remarks
 * Rotate the observability plugin's hooks-scoped ingest credential. Mints a replacement key, republishes the marketplace when possible so installs pick it up, and either revokes previous plugin hooks keys immediately or keeps them valid for a grace window.
 */
export function useRotateObservabilityCredentialMutation(
  options?: MutationHookOptions<
    RotateObservabilityCredentialMutationData,
    RotateObservabilityCredentialMutationError,
    RotateObservabilityCredentialMutationVariables
  >,
): UseMutationResult<
  RotateObservabilityCredentialMutationData,
  RotateObservabilityCredentialMutationError,
  RotateObservabilityCredentialMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRotateObservabilityCredentialMutation(client, options),
    ...options,
  });
}

export function mutationKeyRotateObservabilityCredential(): MutationKey {
  return ["@gram/client", "plugins", "rotateObservabilityCredential"];
}

export function buildRotateObservabilityCredentialMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RotateObservabilityCredentialMutationVariables,
  ) => Promise<RotateObservabilityCredentialMutationData>;
} {
  return {
    mutationKey: mutationKeyRotateObservabilityCredential(),
    mutationFn: function rotateObservabilityCredentialMutationFn({
      request,
      security,
      options,
    }): Promise<RotateObservabilityCredentialMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(pluginsRotateObservabilityCredential(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
